<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <b-dropdown
            v-if="getUserPermission('EXPORT_DAILY_TILL_BALANCE_REPORT')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            :text="$t('Export')"
            variant="primary"
          >
            <b-dropdown-item @click="csvExport(csvData)">
              {{ $t('CSV') }}
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport(csvData)">
              {{ $t('Excel') }}
            </b-dropdown-item>
            <b-dropdown-item @click="pdfExport">
              {{ $t('PDF') }}
            </b-dropdown-item>
          </b-dropdown></b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="dialyTillBalanceTableRef"
      :items="reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="tillNumber"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>
      <template #cell(financialDate)="data">
        <span class="text-nowrap">
          {{ data.value ? formatCustomDateYear(data.value) : '' }}
        </span>
      </template>
      <template #cell(openingBalance)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>

      <template #cell(totalCashDebits)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>

      <template #cell(totalCashCredits)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>

      <template #cell(totalOtherDebits)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>

      <template #cell(totalOtherCredits)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>

      <template #cell(closingBalance)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      no-close-on-backdrop
      :title="$t('Filters')"
      modal-footer
      @ok="handleSubmit"
      @cancel="resetFilters"
    >
      <validation-observer ref="dailyTillBalanceRules">
        <b-form>
          <b-row class="match-height">
            <!-- Till Name -->
            <b-col md="6">
              <InputField
                id="till-name"
                v-model="filter.tillName"
                :label="$t('Till Name')"
                :placeholder="$t('Enter Till Name')"
                type="text"
                for="till-name"
              />
            </b-col>
            <!-- Till Name -->
            <!-- Till Number -->
            <b-col md="6">
              <InputField
                id="till-number"
                v-model="filter.tillNumber"
                :label="$t('Till Number')"
                :placeholder="$t('Enter Till Number')"
                type="text"
                for="till-number"
              />
            </b-col>
          <!-- Till Number -->
          </b-row>

          <b-row class="match-height">
            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="branch"
                v-model="filter.branchCode"
                :reduce="option => option.value"
                :label="$t('Branch')"
                :placeholder="$t('Select')"
                :options="branchOptions"
                item="label"
                @input="getBranchValue(filter.branchCode)"
              />
            </b-col>
            <!-- Branch -->

            <b-col md="6 mt-2">
              <SwitchButton
                v-model="filter.useCurrentFinancialDate"
                :label="$t('Use Current Financial Date')"
                name="use-current-financial-date"
                :readonly="isReadonly"
                @input="setUseCurrentFinancialDate(filter.useCurrentFinancialDate)"
              />
            </b-col>
          </b-row>
          <b-row class="match-height">
            <!-- Start Date -->
            <b-col md="6">
              <DatePickerField
                id="financial-date-from"
                v-model="filter.financialDateFrom"
                rules="required"
                :min-date="new Date()"
                :label="$t('Financial Date (From)')"
                :error-name="$t('Financial Date From')"
                :config="datePickerConfig"
                :placeholder="$t('Date From')"
                :disabled="isDisabled"
              />
            </b-col>
            <!-- Start Date -->
            <!-- End Date -->
            <b-col md="6">
              <DatePickerField
                id="financial-date-to"
                v-model="filter.financialDateTo"
                rules="required"
                :min-date="new Date()"
                :label="$t('Financial Date (To)')"
                :error-name="$t('Financial Date To')"
                :config="datePickerConfig"
                :placeholder="$t('Date To')"
                :disabled="isDisabled"
              />
            </b-col>
          <!-- End Date -->
          </b-row>
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BModal, BForm, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { tableMixins } from '@core/mixins/table'
import { modalMixins } from '@core/mixins/modal'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { mapGetters, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import XLSX from 'xlsx'
import { heightFade } from '@core/directives/animations'
import { toBalance, formatAmount } from '@/helpers/formatAmount'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import SwitchButton from '../../../components/switch/Switch.vue'
import InputField from '../../../forms/InputField.vue'
import SelectField from '../../../forms/SelectField.vue'
import toNumber from '../../../../helpers/toNumber'
import { customDate, toCustomDate } from '../../../../helpers/formatDate'
import Button from '../../../components/button/Button.vue'
import { reportDataDetails } from '../../../../helpers/data'
import { reportPermissions } from '../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    TablePagination,
    InputField,
    SelectField,
    BModal,
    BForm,
    BDropdown,
    BDropdownItem,
    TableEntries,
    ValidationObserver,
    DatePickerField,
    SwitchButton,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      isDisabled: false,
      query: '',
      statusToggleLabel: '',
      isReadonly: false,
      buttonLabel: this.$t('Save'),
      isEditable: false,
      isBusy: false,
      required,
      modalTitle: this.$t('Daily Till Balance Report'),
      sortBy: 'name',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'tillName', sortable: true, label: 'Till Name' },
        { key: 'tillNumber', sortable: true, label: 'Till Number' },
        { key: 'tellerName', sortable: true, label: 'Teller\'s Name' },
        { key: 'branch', sortable: true, label: 'Branch' },
        { key: 'financialDate', sortable: true, label: 'Financial Date' },
        { key: 'openingBalance', sortable: true, label: 'Opening Balance' },
        { key: 'totalCashDebits', sortable: true, label: 'Total Cash Debits' },
        { key: 'totalCashCredits', sortable: true, label: 'Total Cash Credits' },
        { key: 'totalOtherDebits', sortable: true, label: 'Total Other Debits' },
        { key: 'totalOtherCredits', sortable: true, label: 'Total Other Credits' },
        { key: 'closingBalance', sortable: true, label: 'Closing Balance' },
      ],
      filter: {
        tillNumber: '',
        tillName: '',
        financialDateFrom: null,
        financialDateTo: null,
        branchCode: '',
        useCurrentFinancialDate: true,
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: 'today',
        defaultDate: '',
      },
      productOptions: [],
      reportData: [],
      allSearchedData: [],
      branchRefCode: '',
      selected: '',
      reportInfo: reportDataDetails,
    }
  },
  computed: {
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
      branchOptions: 'operations/branchOptions',
    }),
    csvData() {
      return this.exportItems.map(item => ({
        ...item,
      }))
    },
    exportItems() {
      return this.allSearchedData.map(item => ({
        'Till Number': item.tillName,
        'Till Name': item.tillNumber,
        'Teller\'s Name': item.tellerName,
        Branch: item.branch,
        'Financial Date': this.formatCustomDateYear(item.financialDate),
        'Opening Balance': this.toAmount(item.openingBalance),
        'Total Cash Debits': this.toAmount(item.totalCashDebits),
        'Total Cash Credits': this.toAmount(item.totalCashCredits),
        'Total Other Debits': this.toAmount(item.totalOtherDebits),
        'Total Other Credits': this.toAmount(item.totalOtherCredits),
        'Closing Balance': this.toAmount(item.closingBalance),
      }))
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchDailyTillBalanceReport()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchDailyTillBalanceReport()
    },
  },
  mounted() {
    this.fetchProduct()
    this.fetchDailyTillBalanceReport()
  },
  methods: {
    ...mapActions({
      getProduct: 'account/getProducts',
      getBranchByCode: 'operations/getBranchByCode',
      getDailyTillBalance: 'reports/getDailyTillBalance',
    }),

    resetFilters() {
      this.filter = { ...null }
      this.filter.financialDateFrom = null
      this.filter.financialDateTo = null
      this.filter.useCurrentFinancialDate = true
    },
    resetFilter() {
      this.filter = { ...null }
      this.filter.financialDateFrom = null
      this.filter.financialDateTo = null
      this.filter.useCurrentFinancialDate = true
      this.fetchDailyTillBalanceReport()
    },

    setUseCurrentFinancialDate(dataValue) {
      if (dataValue === false) {
        this.isDisabled = false
      } else {
        this.isDisabled = true
        this.filter.financialDateFrom = null
        this.filter.financialDateTo = null
      }
    },
    async fetchProduct() {
      try {
        const response = await this.getProduct({
          search: '',
          page: 0,
          limit: 0,
          permission: reportPermissions.DAILY_TILL_BALANCE_REPORT,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.productOptions = data.data.map(item => ({ label: item.name, value: item.code }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },

    async getBranchValue(dataValue) {
      if (dataValue !== null) {
        try {
          const response = await this.getBranchByCode(dataValue)
          const { status, data } = response
          if (status === 200 && data) {
            this.branchRefCode = data
          }
        } catch (error) {
          this.alertError({ text: error })
        } finally {
          this.isBusy = false
        }
      }
    },
    async handleSubmit(bvModalEvt) {
      if (this.filter.useCurrentFinancialDate === false) {
        bvModalEvt.preventDefault()
        this.$refs.dailyTillBalanceRules.validate().then(async success => {
          if (success) {
            this.fetchDailyTillBalanceReport()
          }
        })
      } else {
        this.fetchDailyTillBalanceReport()
      }
    },
    async fetchDailyTillBalanceReport() {
      try {
        this.hideModal('modal-filter')
        this.isBusy = true
        const response = await this.getDailyTillBalance({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.reportData = data.data
          this.totalCount = data.recordCount
          this.isBusy = false
          this.isDisabled = false
        } else {
          this.reportData = []
          this.totalCount = 0
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },

    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.getDailyTillBalance({
          search: this.filter,
          page: 1,
          limit: 100000,
        })
        const { status, data } = response
        if (status === 200) {
          this.allSearchedData = data.data
          this.isBusy = false
          this.isDisabled = false
          const arrData = this.exportItems.map(item => ({ ...item }))
          const orgName = this.userDetails.institutionName.toUpperCase()
          let csvContent = 'data:text/csv;charset=utf-8,'
          csvContent += [
            [orgName],
            ['Daily Till Balance Report'],
            [`${(this.filter.financialDateFrom !== null && this.filter.financialDateTo !== null) ? `Date Range: ${this.formatCustomDate(this.filter.financialDateFrom)} - ${this.formatCustomDate(this.filter.financialDateTo)}` : 'Date Range: All'}`],
            [`${(this.branchRefCode !== '') ? `${this.branchRefCode.name} - ${this.branchRefCode.code}` : 'Branch: All'}`],
            [],

            Object.keys(arrData[0]).join(','),
            ...arrData.map(item => Object.values(item).join(',')),
          ]
            .join('\n')
            .replace(/(^\[)|(\]$)/gm, '')

          const dataValue = encodeURI(csvContent)
          const link = document.createElement('a')
          link.setAttribute('href', dataValue)
          link.setAttribute('download', 'Daily-Till-Balance-Report.csv')
          link.click()
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },

    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.getDailyTillBalance({
          search: this.filter,
          page: 1,
          limit: 100000,
        })
        const { status, data } = response
        if (status === 200) {
          this.allSearchedData = data.data
          this.isBusy = false
          this.isDisabled = false
          const arrData = this.exportItems.map(item => ({ ...item }))
          const orgName = this.userDetails.institutionName.toUpperCase()
          const filename = 'Daily-Till-Balance-Report.xlsx'
          const headerLists = [
            Object.keys(arrData[0]),
          ]

          const Heading = [
            [orgName],
            ['Daily Till Balance Report'],
            [`${(this.filter.financialDateFrom !== null && this.filter.financialDateTo !== null) ? `Date Range: ${this.formatCustomDate(this.filter.financialDateFrom)} - ${this.formatCustomDate(this.filter.financialDateTo)}` : 'Date Range: All'}`],
            [`${(this.branchRefCode !== '') ? `${this.branchRefCode.name} - ${this.branchRefCode.code}` : 'Branch: All'}`],
            [],
            headerLists[0], // Headers lists
          ]
          const Data = arrData // All data lists
          const ws = XLSX.utils.aoa_to_sheet(Heading)
          XLSX.utils.sheet_add_json(ws, Data, {
            skipHeader: true,
            origin: -1,
          })
          const wb = XLSX.utils.book_new()
          XLSX.utils.book_append_sheet(wb, ws, 'Daily Till Balance Report')
          XLSX.writeFile(wb, filename)
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async pdfExport() {
      try {
        this.isBusy = true
        const response = await this.getDailyTillBalance({
          search: this.filter,
          page: 1,
          limit: 100000,
        })
        const { status, data } = response
        if (status === 200) {
          this.isBusy = false
          this.isDisabled = false
          const pdfData = data.data.map(item => ({
            ...item,
            openingBalance: this.toAmount(item.openingBalance),
            totalCashDebits: this.toAmount(item.totalCashDebits),
            totalCashCredits: this.toAmount(item.totalCashCredits),
            totalOtherDebits: this.toAmount(item.totalOtherDebits),
            totalOtherCredits: this.toAmount(item.totalOtherCredits),
            closingBalance: this.toAmount(item.closingBalance),
            financialDate: item.financialDate ? this.formatCustomDate(item.financialDate) : '',
          }))

          const columns = [
            { title: 'Till Name', key: 'tillName' },
            { title: 'Till Number', key: 'tillNumber' },
            { title: 'Teller Name', key: 'tellerName' },
            { title: 'Branch', key: 'branch' },
            { title: 'Fin. Date', key: 'financialDate' },
            { title: 'Opening Bal.', key: 'openingBalance' },
            { title: 'Total Cash Debits', key: 'totalCashDebits' },
            { title: 'Total Cash Credits', key: 'totalCashCredits' },
            { title: 'Total Other Debits', key: 'totalOtherDebits' },
            { title: 'Total Other Credits', key: 'totalOtherCredits' },
            { title: 'Closing Bal.', key: 'closingBalance' },
          ]

          const doc = jsPDF('landscape', 'pt', 'a4')
          const logo = this.reportInfo.map(item => item.orgLogo)
          const orgName = this.userDetails.institutionName.toUpperCase()
          const orgLogo = `${logo}`
          doc.addImage(orgLogo, 'JPEG', 40, 30)
          doc.setFontSize(20)
          doc.text(180, 60, `${orgName}`)
          doc.setFontSize(15)
          doc.text(180, 80, 'Daily Till Balance Report')
          doc.setFontSize(12)
          doc.text(180, 95, `${(this.filter.financialDateFrom !== null && this.filter.financialDateTo !== null) ? `Date Range: ${this.formatCustomDate(this.filter.financialDateFrom)} - ${this.formatCustomDate(this.filter.financialDateTo)}` : 'Date Range: All'}`)
          doc.text(180, 110, `${(this.branchRefCode !== '') ? `${this.branchRefCode.name} - ${this.branchRefCode.code}` : 'Branch: All'}`)
          doc.autoTable({
            theme: 'striped', // grid, plain
            columns,
            body: pdfData,
            startY: 150,
            // split overflowing columns into pages
            horizontalPageBreak: true,
            // repeat this column in split pages
            horizontalPageBreakRepeat: 'id',
            tableWidth: 'auto', // 'auto', 'wrap' or a number,
            margin: {
              horizontal: 20,
              vertical: 20,
            },
            bodyStyles: {
              valign: 'top',
            },
            styles: {
              overflow: 'linebreak',
              cellWidth: 'wrap',
              maxCellHeight: 4,
              fontSize: 8,
              cellPadding: 5,
            },
            columnStyles: {
              text: {
                cellWidth: 'wrap',
              },
            },
          })
          // Footer page number
          const pages = doc.internal.getNumberOfPages()
          const pageWidth = doc.internal.pageSize.width
          const pageHeight = doc.internal.pageSize.height
          doc.setFontSize(5)

          for (let j = 1; j < pages + 1; j += 1) {
            const horizontalPos = pageWidth / 2 // Can be fixed number
            const verticalPos = pageHeight - 10 // Can be fixed number
            doc.setPage(j)
            doc.text(`page ${j} of ${pages}`, horizontalPos, verticalPos, { align: 'center' }) // Optional text styling});
          }
          doc.save('Daily-Till-Balance-Report.pdf')
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },

    formatAmount: toBalance,
    toAmount: formatAmount,
    formatNumber: toNumber,
    formatCustomDateYear: customDate,
    formatCustomDate: toCustomDate,
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}
</style>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
</style>
